import { EmbeddedCompletionEnum } from '@vedalib/editor/lib/elements'
import { useInViewport } from 'ahooks'
import cn from 'classnames'
import lodash from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'

import { SHELL_SIZE_KEYS } from 'components/Editor/types/data.constants'
import Empty from 'components/uiKit/Empty'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { isElementCompleteMessage } from 'services/IframeMessaging'
import { useProjectT } from 'services/ProjectLabels'
import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'

import { useElementCompleted } from '../../hooks/useElementCompleted'
import { useElementRootStyles } from '../../hooks/useElementRootStyles'
import * as s from './EmbeddedElement.module.scss'
import { DEFAULT_IFRAME_HEIGHT, IFRAME_MAX_HEIGHT, IFRAME_MIN_HEIGHT } from './constants'
import type { EmbeddedElementType } from './embeddedTypes'
import {
  getInnerIframeData,
  getNumberFromString,
  isEmptyValue,
  makeCustomScormApi,
  prepareHtml,
  scormBridgeScript,
} from './helper'
import { useIframeAutoHeight } from './hooks/useIframeAutoHeight'

const EMPTY_MAP = {
  html: {
    icon: () => <Icon color='#BDBDBD' name='iconsOtherCode' size={KitSize.L} />,
    text: t('elements.embedded.codeEmpty'),
  },
  link: {
    icon: () => <Icon color='#BDBDBD' name='iconBuilderLink' size={KitSize.L} />,
    text: t('elements.embedded.linkEmpty'),
  },
  zip: {
    icon: () => <Icon color='#BDBDBD' name='otherFile' size={KitSize.L} />,
    text: t('elements.embedded.fileEmpty'),
  },
}
const V2_SHELL: React.CSSProperties = {
  width: '100%',
  height: '100%',
  minWidth: '100%',
  minHeight: '100%',
}
const EmbeddedElement: EmbeddedElementType = ({ styles, element, mode, block, isV2 }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const pt = useProjectT()
  const sizeStyles = useMemo(
    () => (isV2 ? V2_SHELL : lodash.pick(styles.shell, SHELL_SIZE_KEYS)),
    [isV2, styles.shell],
  )
  const { iframeRef, iframeId, height } = useIframeAutoHeight({
    defaultHeight: DEFAULT_IFRAME_HEIGHT,
    minHeight: getNumberFromString(sizeStyles.minHeight, IFRAME_MIN_HEIGHT),
    maxHeight: getNumberFromString(sizeStyles.maxHeight, IFRAME_MAX_HEIGHT),
  })
  const ref = useRef<HTMLDivElement | null>(null)
  const [inViewPort] = useInViewport(ref)

  const rootStyles = useElementRootStyles(styles.root)
  const isEditor = mode.previewMode === PreviewMode.editor

  const isScorm = mode.previewMode === PreviewMode.scorm
  const isScormCompletion = element.value.completion === EmbeddedCompletionEnum.scormPassed
  const isViewedPassEl = element.value.completion === EmbeddedCompletionEnum.none && inViewPort
  const isClickPassed = element.value.completion === EmbeddedCompletionEnum.click
  const innerIframeData = getInnerIframeData(element.value, iframeId, () => blobUrl || '')
  const bridgeScript = useMemo(() => scormBridgeScript(element.id), [element.id])
  const showIframe = innerIframeData.onlyScorm ? isScorm : true

  const isZipContent = element.value.type === 'zip'
  const isFileProtocol = window.location.protocol === 'file:'
  // Use ScormBridge for zip content to isolate nested courses, but not for file:// protocol
  // because blob URLs cannot load file:// resources due to browser CORS restrictions
  const needsScormBridge = (isScormCompletion || isZipContent) && !isFileProtocol

  const iframeSrc = useMemo(() => {
    return needsScormBridge
      ? makeCustomScormApi(innerIframeData.props.src || '', element.id)
      : innerIframeData.props.src
  }, [needsScormBridge, innerIframeData.props.src, element.id])

  const empty = isEmptyValue(element.value)

  const setComplete = useElementCompleted({
    block,
    elementId: element.id,
    completed: false,
    deviceMode: mode.deviceMode,
  })

  useEffect(() => {
    const iframe = iframeRef.current

    if (isViewedPassEl) {
      setComplete(true)
    }

    const handleMessage = (event: MessageEvent) => {
      if (isElementCompleteMessage(event.data) && event.data.id === element.id) {
        setComplete(true)
      }
    }

    const handleClick = () => isClickPassed && setComplete(true)

    const handleWindowBlur = () =>
      document.activeElement === iframe && isClickPassed && setComplete(true)

    const handleLoad = () => iframe?.contentDocument?.body?.addEventListener('click', handleClick)

    window.addEventListener('message', handleMessage)
    iframe?.addEventListener('load', handleLoad)
    window.addEventListener('blur', handleWindowBlur)

    return () => {
      window.removeEventListener('message', handleMessage)
      iframe?.removeEventListener('load', handleLoad)
      const iframeBody = iframe?.contentDocument?.body
      iframeBody?.removeEventListener('click', handleClick)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [isViewedPassEl, isClickPassed, iframeRef, setComplete, element.id])

  useEffect(() => {
    if (element.value.type === 'html' && element.value.html) {
      const htmlString = prepareHtml(element.value.html, iframeId, bridgeScript)
      const blob = new Blob([htmlString], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      setBlobUrl(url)

      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setBlobUrl(null)
      return () => {}
    }
  }, [element.value.html, element.value.type, iframeId, isScormCompletion, bridgeScript])

  if (empty) {
    return (
      <div className={cn(s.root, s.empty)} ref={ref} style={{ ...rootStyles, ...sizeStyles }}>
        <Empty
          CustomIcon={EMPTY_MAP[element.value.type]?.icon}
          size={KitSize.S}
          text={EMPTY_MAP[element.value.type]?.text}
          grow
        />
      </div>
    )
  }

  return (
    <div className={s.root} ref={ref} style={{ ...rootStyles, ...sizeStyles }}>
      {!showIframe && <div>{innerIframeData.editorMessage}</div>}
      {showIframe && (
        <iframe
          className={s.iframe}
          ref={iframeRef}
          sandbox='allow-scripts allow-same-origin allow-forms allow-presentation allow-popups allow-popups-to-escape-sandbox'
          src={iframeSrc}
          style={{ ...sizeStyles, height: `${height}px` }}
          title='Embedded content'
        >
          <p>{pt('elements.embedded.notSupported')}</p>
        </iframe>
      )}
      {isEditor && <div className={s.overlay} />}
    </div>
  )
}

export default EmbeddedElement
